import type { ParsedLabReportDto, ParsedLabReportSizeClassDto } from "@partikelart/environment/dtos";

/**
 * Parser for CleanControlling lab report PDFs ("Restschmutzanalyse /
 * Sauberkeitsanalyse", template FB25-36/2).
 *
 * Works on the plain text extracted from the PDF (one string per page).
 * All regexes anchor on the bilingual field labels so column/reading-order
 * quirks of pdf.js text extraction don't break the parse. Every field is
 * optional — missing fields produce a warning and the user fixes them in
 * the review step.
 */

export interface ParseLabReportResult {
	parsed: ParsedLabReportDto;
	warnings: string[];
}

/** Parse a German-formatted number ("191,3", "1519,92", "1.234,5"). */
export function parseGermanNumber(raw: string): number | null {
	const s = raw.trim().replace(/\s/g, "");
	if (!s || s === "-") return null;
	const normalized = s.includes(",") ? s.replace(/\./g, "").replace(",", ".") : s;
	const n = Number(normalized);
	return Number.isFinite(n) ? n : null;
}

/** Parse "dd.mm.yyyy" into an ISO date string (yyyy-mm-dd). */
export function parseGermanDate(raw: string): string | null {
	const m = raw.trim().match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
	if (!m) return null;
	return `${m[3]}-${m[2]}-${m[1]}`;
}

/** Combine "dd.mm.yyyy" + "hh:mm" into a local ISO datetime string. */
export function parseGermanDateTime(date: string, time: string): string | null {
	const isoDate = parseGermanDate(date);
	const t = time.trim().match(/^(\d{1,2}):(\d{2})/);
	if (!isoDate || !t) return isoDate;
	return `${isoDate}T${t[1].padStart(2, "0")}:${t[2]}:00`;
}

/** The eleven size classes of the FB25-36/2 distribution table. */
const SIZE_CLASS_ROWS: { range: string; key: string; lower: number; upper: number | null }[] = [
	{ range: "25-50", key: "D", lower: 25, upper: 50 },
	{ range: "50-100", key: "E", lower: 50, upper: 100 },
	{ range: "100-150", key: "F", lower: 100, upper: 150 },
	{ range: "150-200", key: "G", lower: 150, upper: 200 },
	{ range: "200-400", key: "H", lower: 200, upper: 400 },
	{ range: "400-600", key: "I", lower: 400, upper: 600 },
	{ range: "600-1000", key: "J", lower: 600, upper: 1000 },
	{ range: "1000-1500", key: "K", lower: 1000, upper: 1500 },
	{ range: "1500-2000", key: "L", lower: 1500, upper: 2000 },
	{ range: "2000-3000", key: "M", lower: 2000, upper: 3000 },
	{ range: ">3000", key: "N", lower: 3000, upper: null },
];

function escapeRegex(s: string): string {
	return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Match a label followed (possibly on the next line) by a captured value. */
function matchAfter(text: string, label: RegExp, value: RegExp): string | null {
	const m = text.match(new RegExp(`${label.source}\\s*\\n?\\s*${value.source}`, "i"));
	return m ? m[1] : null;
}

/**
 * Extract the "particles per trap" size-class table (integer counts) for
 * metallic / non-metallic columns. The second table on the page (per
 * 1000 cm²) uses decimal commas, so requiring integer-only columns
 * selects the correct table.
 */
function parseDistributionTable(text: string): Map<string, { metallic: number; nonMetallic: number }> {
	const result = new Map<string, { metallic: number; nonMetallic: number }>();
	for (const row of SIZE_CLASS_ROWS) {
		const re = new RegExp(`^${escapeRegex(row.range)}\\s+${row.key}\\s+(\\d+)\\s+(\\d+)\\s+(\\d+)\\s*$`, "m");
		const m = text.match(re);
		if (m) {
			result.set(row.key, { metallic: Number(m[1]), nonMetallic: Number(m[2]) });
		}
	}
	return result;
}

/**
 * Extract the fibers table: `range key fibersPerTrap fibersPer1000cm² allWithFibers`.
 * The per-1000cm² column always carries a decimal comma ("0,0", "65,8"),
 * which distinguishes fiber rows from the integer-only distribution table.
 */
function parseFiberTable(text: string): Map<string, number> {
	const result = new Map<string, number>();
	for (const row of SIZE_CLASS_ROWS) {
		const re = new RegExp(`^${escapeRegex(row.range)}\\s+${row.key}\\s+(\\d+)\\s+(\\d+,\\d+)\\s+(\\d+)\\s*$`, "m");
		const m = text.match(re);
		if (m) {
			result.set(row.key, Number(m[1]));
		}
	}
	return result;
}

/** Extract "<length> x <width> μm" particle dimensions after a label. */
function parseParticleDims(text: string, label: RegExp): [number, number] | null {
	const m = text.match(new RegExp(`${label.source}[\\s\\S]{0,40}?([\\d.,]+)\\s*x\\s*([\\d.,]+)\\s*[μµ]m`, "i"));
	if (!m) return null;
	const length = parseGermanNumber(m[1]);
	const width = parseGermanNumber(m[2]);
	return length !== null && width !== null ? [length, width] : null;
}

/**
 * Parse the text of a CleanControlling lab report PDF.
 *
 * @param pages plain text of each PDF page (from unpdf `extractText`)
 */
export function parseLabReport(pages: string[]): ParseLabReportResult {
	const text = pages.join("\n");
	const warnings: string[] = [];

	const analysisNo = matchAfter(text, /Analysis No\.?/, /([\dA-Za-z]+-[\dA-Za-z]+)/);
	const customerNo = matchAfter(text, /Customer No\.?/, /(\S+)/);
	const analyzedAtRaw =
		matchAfter(text, /End of Analysis/, /(\d{2}\.\d{2}\.\d{4})/) ??
		matchAfter(text, /Date of Report/, /(\d{2}\.\d{2}\.\d{4})/);
	const analyzedAt = analyzedAtRaw ? parseGermanDate(analyzedAtRaw) : null;

	const trapNo = matchAfter(text, /Particle trap no\.?/, /(\S+)/);
	const samplingSite = matchAfter(text, /Aufstellungsort\s*\/\s*Sampling site/, /(.+)/);

	// Time lines are usually "11:15 Uhr", but real reports also contain
	// truncated ("12:14 Uh") and bracketed ("[12:29 Uhr]") variants.
	const activation = text.match(/Aktivierung\s*\n(\d{2}\.\d{2}\.\d{4})\s*\n\[?(\d{1,2}:\d{2})/);
	const deactivation = text.match(/Deaktivierung\s*\n(\d{2}\.\d{2}\.\d{4})\s*\n\[?(\d{1,2}:\d{2})/);
	const activatedAt = activation ? parseGermanDateTime(activation[1], activation[2]) : null;
	const deactivatedAt = deactivation ? parseGermanDateTime(deactivation[1], deactivation[2]) : null;

	const exposureRaw = matchAfter(text, /Duration of exposure/, /([\d.,]+)\s*h/);
	const exposureHours = exposureRaw ? parseGermanNumber(exposureRaw) : null;

	const mountingHeightRaw = matchAfter(text, /Aufstellh[öo]he\s*\/\s*Monitoring height/, /(.+)/);
	const mountingHeight = mountingHeightRaw && mountingHeightRaw.trim() !== "-" ? mountingHeightRaw.trim() : null;

	const largestMetallicUm = parseParticleDims(text, /Largest metallic shining particle/);
	const largestNonShiningUm = parseParticleDims(text, /Largest not shining particle/);

	// Page 1 summary: TCV without fibers, value is between the Soll/Ergebnis dashes.
	const tcvRaw = matchAfter(text, /\(Illig-value without Fiber\)/, /-?\s*([\d.,]+)/);
	const tcv = tcvRaw ? parseGermanNumber(tcvRaw) : null;
	// Page 2 values stand alone after the label parenthesis.
	const tcvWithFibersRaw = matchAfter(text, /\(Illig-value with fibers\)/, /([\d.,]+)/);
	const tcvWithFibers = tcvWithFibersRaw ? parseGermanNumber(tcvWithFibersRaw) : null;
	const mcvRaw = matchAfter(text, /\(Illig-value metallic shining particles\)/, /([\d.,]+)/);
	const mcv = mcvRaw ? parseGermanNumber(mcvRaw) : null;
	const fcvRaw = matchAfter(text, /\(Illig-value only fibers\)/, /([\d.,]+)/);
	const fcv = fcvRaw ? parseGermanNumber(fcvRaw) : null;

	const surfaceRaw = matchAfter(text, /Surface of particle-trap/, /([\d.,]+)\s*mm/);
	const surfaceAreaMm2 = surfaceRaw ? parseGermanNumber(surfaceRaw) : null;

	const fiberLengthRaw = matchAfter(text, /Total length of fibers \[mm\]/, /([\d.,]+)/);
	const totalFiberLengthMm = fiberLengthRaw ? parseGermanNumber(fiberLengthRaw) : null;

	const distribution = parseDistributionTable(text);
	const fibers = parseFiberTable(text);

	const sizeClasses: ParsedLabReportSizeClassDto[] = SIZE_CLASS_ROWS.map((row) => ({
		key: row.key,
		label: `${row.range} µm`,
		lower: row.lower,
		upper: row.upper,
		metallicCount: distribution.get(row.key)?.metallic ?? 0,
		nonMetallicCount: distribution.get(row.key)?.nonMetallic ?? 0,
		fibersCount: fibers.get(row.key) ?? 0,
	}));

	// Warnings for missing critical fields
	if (!analysisNo) warnings.push("Analysis number not found");
	if (!analyzedAt) warnings.push("Report date not found");
	if (!trapNo) warnings.push("Particle trap number not found");
	if (!samplingSite) warnings.push("Sampling site not found");
	if (!activatedAt) warnings.push("Activation time not found");
	if (!deactivatedAt) warnings.push("Deactivation time not found");
	if (exposureHours === null) warnings.push("Exposure duration not found");
	if (distribution.size === 0) warnings.push("Particle size distribution table not found");
	if (fibers.size === 0) warnings.push("Fiber size distribution table not found");
	if (tcv === null) warnings.push("TCV not found");

	return {
		parsed: {
			analysisNo,
			customerNo,
			analyzedAt,
			trapNo,
			samplingSite: samplingSite?.trim() ?? null,
			activatedAt,
			deactivatedAt,
			exposureHours,
			mountingHeight,
			largestMetallicUm,
			largestNonShiningUm,
			tcv,
			tcvWithFibers,
			mcv,
			fcv,
			surfaceAreaMm2,
			totalFiberLengthMm,
			sizeClasses,
		},
		warnings,
	};
}
